/* eslint-disable react-hooks/exhaustive-deps */
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useRefetchQuery } from "libs/reactQuery";
import { SocketsContext } from "libs/soket";
import { useContext, useEffect } from "react";

import {
  EscalatedEvent,
  WhatsAppConversation,
  WhatsAppConversationMessagesResponse,
  WhatsAppConversationsListResponse,
  WhatsAppMessage
} from "../aiConversations.types";
import {
  AI_CONVERSATIONS_QUERY_KEY,
  WhatsAppMessageSender
} from "../constants/enums";
import { insertOrReplaceMessage } from "../helpers";
import { useAiTyping } from "./AiTypingProvider";

interface AiGeneratingStartedEvent {
  conversationId: string;
}

type ConversationsInfinite = InfiniteData<WhatsAppConversationsListResponse>;
type MessagesInfinite = InfiniteData<WhatsAppConversationMessagesResponse>;

export const useAiConversationsRealTimeUpdate = () => {
  const { whatsappBotAdminSocket } = useContext(SocketsContext);
  const queryClient = useQueryClient();
  const { addTyping, removeTyping } = useAiTyping();
  const refetchConversations = useRefetchQuery([
    AI_CONVERSATIONS_QUERY_KEY,
    "list"
  ]);

  useEffect(() => {
    const socket = whatsappBotAdminSocket.socket;
    if (!socket) return;

    const patchListWith = (updated: WhatsAppConversation) => {
      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [AI_CONVERSATIONS_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          let touched = false;
          const pages = current.pages.map((page) => {
            if (!page.data.some((c) => c.id === updated.id)) return page;
            touched = true;
            return {
              ...page,
              data: page.data.map((c) =>
                c.id === updated.id
                  ? {
                      ...c,
                      ...updated,
                      unreadCount: c.unreadCount,
                      // The websocket payload skips the identity join, so it
                      // carries no avatar and a display name that degrades to
                      // the bare phone number. Keep what the list already
                      // resolved instead of letting a status change blank it.
                      customerAvatarUrl:
                        c.customerAvatarUrl ?? updated.customerAvatarUrl,
                      customerDisplayName:
                        updated.customerName ??
                        c.customerDisplayName ??
                        updated.customerDisplayName
                    }
                  : c
              )
            };
          });
          if (!touched) return current;
          return { ...current, pages };
        }
      );
    };

    const onConversationCreated = (_data: WhatsAppConversation) => {
      refetchConversations();
    };

    const onConversationUpdated = (data: WhatsAppConversation) => {
      patchListWith(data);
    };

    const onConversationEscalated = (data: EscalatedEvent) => {
      patchListWith(data.conversation);
      removeTyping(data.conversation.id);
    };

    const onAiGeneratingStarted = (data: AiGeneratingStartedEvent) => {
      addTyping(data.conversationId);
    };

    const onMessageCreated = (message: WhatsAppMessage) => {
      if (message.sender === WhatsAppMessageSender.Ai) {
        removeTyping(message.conversationId);
      }

      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [AI_CONVERSATIONS_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((c) => {
                if (c.id !== message.conversationId) return c;
                const isInbound = message.direction === "inbound";
                return {
                  ...c,
                  lastInboundAt: isInbound
                    ? message.createdAt
                    : c.lastInboundAt,
                  unreadCount: isInbound
                    ? (c.unreadCount ?? 0) + 1
                    : c.unreadCount
                };
              })
            }))
          };
        }
      );

      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [
            AI_CONVERSATIONS_QUERY_KEY,
            "messages",
            message.conversationId
          ]
        },
        (current) => insertOrReplaceMessage(current, message)
      );
    };

    socket.on("conversation.created", onConversationCreated);
    socket.on("conversation.updated", onConversationUpdated);
    socket.on("conversation.escalated", onConversationEscalated);
    socket.on("message.created", onMessageCreated);
    socket.on("ai.generating.started", onAiGeneratingStarted);

    return () => {
      socket.off("conversation.created", onConversationCreated);
      socket.off("conversation.updated", onConversationUpdated);
      socket.off("conversation.escalated", onConversationEscalated);
      socket.off("message.created", onMessageCreated);
      socket.off("ai.generating.started", onAiGeneratingStarted);
    };
  }, [whatsappBotAdminSocket.socket]);
};
